import type {
  Category,
  Item,
  LunchAddonLimitCatalog,
  LunchMenuCatalog,
  LunchMenuCatalogItem,
  MenuResponse,
} from "../api/menu";
import dayjs from "dayjs";
import { lunchNow } from "./lunchClock";
import { LUNCH_MENU_GROUP_COLORS, lunchMenuColorIndex, lunchMenuNamesInOrder } from "./lunchMenuColors";

// Обедното меню в каталога на „Маркиране": BE връща активните обедни менюта
// в `lunchMenus` като списък {itemId, цена, размер}. Тук всяко меню става
// ИЗКУСТВЕНА група, а всеки продукт — КОПИЕ на основния артикул/рецепта със
// собствен синтетичен id. Така кошницата, каталогът, търсачката и chips-овете
// работят без промяна, а Кока-Кола от основното меню и Кока-Кола от обедното
// са два различни реда. При изпращане `buildBillElements` праща
// `productId = sourceItemId` + `lunchMenuId` и обедната цена.

// Синтетичните id-та са отрицателни — истинските (org) са положителни и никога
// не се сблъскват с тях.
const LUNCH_ITEM_ID_FACTOR = 1_000_000;

export const lunchItemId = (lunchMenuId: number, itemId: number) =>
  -(lunchMenuId * LUNCH_ITEM_ID_FACTOR + itemId);

export const lunchCategoryId = (lunchMenuId: number) => -lunchMenuId;

/** Истинският артикул/рецепта зад синтетичното id — и когато копието вече го няма. */
export const sourceIdOfLunchItemId = (lunchId: number) => -lunchId % LUNCH_ITEM_ID_FACTOR;

/** Група на обедно меню ли е (отрицателно id). */
export const isLunchCategoryId = (categoryId: number) => categoryId < 0;

export const isLunchItem = (item: Item) => item.lunchMenuId != null;

export const isLunchCategory = (category: Category) => category.id < 0;

/**
 * Обеденият „Единица и размер" на желания език, иначе на български; няма → null
 * (показва се размерът на продукта). Същото правило като кухненския бон.
 */
export const lunchUnitFor = (
  unitLangs: Record<string, string> | null | undefined,
  language: string,
): string | null =>
  unitLangs?.[language]?.trim() || unitLangs?.bg?.trim() || null;

/** Истинският артикул/рецепта зад продукт от каталога (за BE заявки). */
export const sourceItemIdOf = (item: Item) => item.sourceItemId ?? item.id;

// Цветът на групата на менюто — свой за всяко меню (`lib/lunchMenuColors.ts`,
// същият като етикета му). По него каталогът оцветява продуктите на менюто при
// colorBy "group", както всяка друга група.
const buildLunchCategory = (
  lunchMenu: LunchMenuCatalog,
  position: number,
  colorIndex: number,
): Category => ({
  id: lunchCategoryId(lunchMenu.id),
  // Групата носи името на менюто — няколко менюта могат да вървят едновременно
  slug: lunchMenu.name,
  parentId: null,
  // Обедната група е първа в chips-овете и в списъка
  sortIndex: -1000 + position,
  images: null,
  isActive: true,
  color: LUNCH_MENU_GROUP_COLORS[colorIndex],
  translations: (["bg", "en"] as const).map((languageCode) => ({
    id: lunchCategoryId(lunchMenu.id),
    languageCode,
    name: lunchMenu.name,
  })),
});

const buildLunchItem = (
  baseItem: Item,
  lunchMenu: LunchMenuCatalog,
  price: number,
  unitAndSizeLangs: Record<string, string> | null,
  position: number,
  remaining: number | null,
  colorIndex: number,
): Item => ({
  ...baseItem,
  id: lunchItemId(lunchMenu.id, baseItem.id),
  categoryId: lunchCategoryId(lunchMenu.id),
  sortIndex: position,
  price,
  menuId: lunchMenu.id,
  menuPriority: null,
  // Обеденият размер на езика, иначе обеденият на български, иначе на продукта —
  // както кухненският бон и мониторът
  itemLangs: baseItem.itemLangs.map((lang) => ({
    ...lang,
    unitAndSize: lunchUnitFor(unitAndSizeLangs, lang.languageCode) ?? lang.unitAndSize,
  })),
  lunchMenuId: lunchMenu.id,
  sourceItemId: baseItem.id,
  lunchRemaining: remaining,
  lunchMenuName: lunchMenu.name,
  lunchMenuColorIndex: colorIndex,
});

// Ред с порции от `lunchMenus`: основен продукт или опция на добавка
type PortionRow = LunchMenuCatalogItem | LunchAddonLimitCatalog;

/** Порциите, които остават днес (цели); null = без ограничение. */
export const lunchRemainingOf = (row: PortionRow): number | null => {
  const limit = row.dailyLimit ?? 0;
  return limit > 0 ? Math.max(0, Math.floor(limit - (row.soldToday ?? 0))) : null;
};

/** Ключът на порциите — (меню, продукт): всяко меню брои своите. */
export const lunchPairKey = (lunchMenuId: number, productId: number) => `${lunchMenuId}:${productId}`;

/** Продуктът от ключа на порциите. */
export const productOfLunchPairKey = (key: string) => Number(key.split(":")[1]);

/** Ключът на порциите на копие от дневно меню; undefined — обикновен продукт. */
export const lunchKeyOfItem = (item: Pick<Item, "lunchMenuId" | "sourceItemId">) =>
  item.lunchMenuId != null && item.sourceItemId != null
    ? lunchPairKey(item.lunchMenuId, item.sourceItemId)
    : undefined;

/**
 * Колко остават от порциите на активните дневни менюта по ключ (меню, продукт)
 * — основен ред или опция на добавка; ключ без запис = без ограничение. Всяко
 * меню брои само своите: един продукт в две менюта по едно и също време е два
 * отделни брояча.
 */
export interface LunchCounters {
  leftByKey: Record<string, number>;
}

export const lunchCountersOf = (lunchMenus: LunchMenuCatalog[]): LunchCounters => {
  const counters: LunchCounters = { leftByKey: {} };
  for (const lunchMenu of lunchMenus) {
    for (const row of [...lunchMenu.items, ...(lunchMenu.addonLimits ?? [])]) {
      const left = lunchRemainingOf(row);
      if (left != null) counters.leftByKey[lunchPairKey(lunchMenu.id, row.itemId)] = left;
    }
  }
  return counters;
};

/**
 * Колко още може да се добави от продукта, като `inCart` вече е в кошницата.
 * Infinity — не е обеден или е без лимит.
 */
export const lunchRoomLeft = (item: Pick<Item, "lunchRemaining">, inCart: number) =>
  item.lunchRemaining == null ? Infinity : Math.max(0, item.lunchRemaining - inCart);

// ── График ──────────────────────────────────────────────────────────────
// BE връща днешните и предстоящите обедни менюта с графика им; активното се
// избира ТУК по часовника на терминала. Така групата се появява и изчезва
// навреме и без интернет — касата връща последното копие на GET /menu, а
// графикът в него е верен и след часове (и утре).

const ISO_DAY = "YYYY-MM-DD";
// Колко дни напред търсим следващата промяна (седмица + ден стига за всеки график)
const BOUNDARY_SEARCH_DAYS = 8;

const coversDay = (lunchMenu: LunchMenuCatalog, day: dayjs.Dayjs) => {
  const iso = day.format(ISO_DAY);
  if (lunchMenu.dateFrom && iso < lunchMenu.dateFrom) return false;
  if (lunchMenu.dateTo && iso > lunchMenu.dateTo) return false;
  return !lunchMenu.weekdays?.length || lunchMenu.weekdays.includes(day.day());
};

/** Активно ли е менюто в момента `now` (дата, ден, ЧЧ:ММ — „до" включително). */
export const isLunchMenuActiveAt = (lunchMenu: LunchMenuCatalog, now: Date) => {
  const moment = dayjs(now);
  if (!coversDay(lunchMenu, moment)) return false;
  const clock = moment.format("HH:mm");
  if (lunchMenu.timeFrom && clock < lunchMenu.timeFrom) return false;
  if (lunchMenu.timeTo && clock > lunchMenu.timeTo) return false;
  return true;
};

/** Следващият момент след `now`, в който обедно меню се появява или изчезва. */
export const nextLunchBoundaryAt = (lunchMenus: LunchMenuCatalog[], now: Date): Date | null => {
  const start = dayjs(now).startOf("day");
  for (let offset = 0; offset < BOUNDARY_SEARCH_DAYS; offset += 1) {
    const day = start.add(offset, "day");
    const upcoming = lunchMenus
      .filter((lunchMenu) => coversDay(lunchMenu, day))
      .flatMap((lunchMenu) => {
        const [fromHour, fromMinute] = (lunchMenu.timeFrom ?? "00:00").split(":").map(Number);
        const [toHour, toMinute] = (lunchMenu.timeTo ?? "23:59").split(":").map(Number);
        // „до 14:59" = до края на минутата → изчезва в 15:00:00
        return [
          day.hour(fromHour).minute(fromMinute).valueOf(),
          day.hour(toHour).minute(toMinute).add(1, "minute").valueOf(),
        ];
      })
      .filter((at) => at > now.getTime());
    if (upcoming.length > 0) return new Date(Math.min(...upcoming));
  }
  return null;
};

/** Продава ли се `productId` от обедното меню `lunchMenuId` в момента `now`. */
export const isLunchItemOnSale = (
  menu: MenuResponse | undefined,
  lunchMenuId: number,
  productId: number,
  now: Date = lunchNow(),
) =>
  (menu?.lunchMenus ?? []).some(
    (lunchMenu) =>
      lunchMenu.id === lunchMenuId &&
      isLunchMenuActiveAt(lunchMenu, now) &&
      lunchMenu.items.some((item) => item.itemId === productId) &&
      menu!.items.some((item) => item.id === productId),
  );

/** id-тата на активните в момента менюта — за да се види дали нещо се е сменило. */
export const activeLunchMenuKey = (lunchMenus: LunchMenuCatalog[], now: Date) =>
  lunchMenus
    .filter((lunchMenu) => isLunchMenuActiveAt(lunchMenu, now))
    .map((lunchMenu) => lunchMenu.id)
    .join(",");

/**
 * Добавя АКТИВНИТЕ в момента `now` обедни менюта като групи + копия на
 * продуктите им. `lunchMenus` остава целият (графикът трябва на часовника).
 */
export const withLunchMenus = (menu: MenuResponse, now: Date): MenuResponse => {
  const lunchMenus = (menu.lunchMenus ?? []).filter((lunchMenu) =>
    isLunchMenuActiveAt(lunchMenu, now),
  );
  if (lunchMenus.length === 0) return menu;
  const lunchCounters = lunchCountersOf(lunchMenus);
  // Цветът — по всички менюта от отговора, не само активните: менюто не сменя
  // цвета си, когато друго започне или свърши през деня.
  const lunchMenuNames = lunchMenuNamesInOrder(menu.lunchMenus ?? []);
  const itemsById = new Map(menu.items.map((item) => [item.id, item]));
  const lunchCategories: Category[] = [];
  const lunchItems: Item[] = [];
  lunchMenus.forEach((lunchMenu, menuPosition) => {
    const colorIndex = lunchMenuColorIndex(lunchMenu.name, lunchMenuNames);
    const copies = lunchMenu.items.flatMap((lunchItem, position) => {
      const baseItem = itemsById.get(lunchItem.itemId);
      return baseItem
        ? [
            buildLunchItem(
              baseItem,
              lunchMenu,
              lunchItem.price,
              lunchItem.unitAndSizeLangs,
              position,
              lunchRemainingOf(lunchItem),
              colorIndex,
            ),
          ]
        : [];
    });
    if (copies.length === 0) return;
    lunchCategories.push(buildLunchCategory(lunchMenu, menuPosition, colorIndex));
    lunchItems.push(...copies);
  });
  return {
    ...menu,
    items: [...menu.items, ...lunchItems],
    categories: [...lunchCategories, ...menu.categories],
    lunchCounters,
  };
};
